import { etagFromRow, get, getVersioned, post, postVersioned, putVersioned, seg } from '../client';
import type { ListParams, Page } from '../types/common';
import type {
  BackendFulfillmentState, CreateOrderRequest, FulfillmentState, Order, OrderCheckResult, OrderFulfillment, OrderSet, OrderTransitionRequest, OrderVersion,
  Orderable, SignOrderRequest, UpdateOrderRequest,
} from '../types/orders';

const ocmd = (id: string, c: string) => `/orders/${seg(id)}:${c}`;

const LEGACY_FULFILLMENT: Record<string, BackendFulfillmentState> = { in_progress: 'performed', completed: 'closed' };

/** Map legacy (mock) fulfilment values onto the backend vocabulary; unknown values pass through untouched. */
export function normalizeFulfillment(s: FulfillmentState | string | null | undefined): FulfillmentState | null {
  if (s == null) return null;
  return (LEGACY_FULFILLMENT[s] ?? s) as FulfillmentState;
}

/** Backend Order DTO → UI Order. The backend shape is already the UI shape; this fills defaults and maps legacy values. */
export function normalizeOrder(o: Order): Order {
  if (!o || typeof o !== 'object') return o;
  return {
    ...o,
    answers: o.answers ?? {},
    overrides: o.overrides ?? [],
    note: o.note ?? '',
    medication: o.medication ?? null,
    indication: o.indication ?? null,
    replacesId: o.replacesId ?? null,
    fulfillment: normalizeFulfillment(o.fulfillment),
  };
}

const normPage = (p: Page<Order>): Page<Order> => ({ ...p, items: (p.items ?? []).map(normalizeOrder) });
const normV = <T extends { data: Order; etag: string | null }>(r: T): T => ({ ...r, data: normalizeOrder(r.data), etag: r.etag ?? etagFromRow(r.data?.rowVersion) });

/** Backend requires a reason (min 3 characters) for cancel / discontinue / entered-in-error. */
export const DRAFT_REMOVAL_REASON = 'Removed before signing';

export const ordersApi = {
  orderables: (q: string, kind?: string, limit = 50) => get<Page<Orderable>>('/orderables', { q, kind, limit }),
  orderSets: () => get<Page<OrderSet>>('/order-sets'),
  list: async (patientId: string, p: ListParams & { status?: string } = {}) => {
    const { status, ...rest } = p;
    return normPage(await get<Page<Order>>(`/patients/${seg(patientId)}/orders`, { ...rest, status: status && status !== 'all' ? status : undefined }));
  },
  create: async (body: CreateOrderRequest) => normV(await postVersioned<Order>('/orders', body, { idempotent: true })),
  get: async (id: string) => normV(await getVersioned<Order>(`/orders/${seg(id)}`)),
  update: async (id: string, body: UpdateOrderRequest, etag: string | null) => normV(await putVersioned<Order>(`/orders/${seg(id)}`, body, { ifMatch: etag })),
  /** Draft → pended (awaiting a provider signature). Nurse/MA orders are pended at creation. */
  pend: async (id: string, etag: string | null) => normV(await postVersioned<Order>(ocmd(id, 'pend'), {}, { ifMatch: etag, idempotent: true })),
  /** Deterministic safety evaluation (runs even when AI is off). No If-Match (read-only evaluation). */
  check: (id: string) => post<OrderCheckResult>(ocmd(id, 'check'), {}),
  /** Step-up required. 422 ETNAS_SAFETY_CHECK (problem.findings) when warnings lack override reasons. */
  sign: async (id: string, body: SignOrderRequest, etag: string | null, label: string) =>
    normV(await postVersioned<Order>(ocmd(id, 'sign'), body, { ifMatch: etag, idempotent: true, stepUpLabel: label })),
  hold: async (id: string, body: OrderTransitionRequest, etag: string | null) => normV(await postVersioned<Order>(ocmd(id, 'hold'), body, { ifMatch: etag, idempotent: true })),
  resume: async (id: string, etag: string | null) => normV(await postVersioned<Order>(ocmd(id, 'resume'), {}, { ifMatch: etag, idempotent: true })),
  discontinue: async (id: string, body: OrderTransitionRequest, etag: string | null) =>
    normV(await postVersioned<Order>(ocmd(id, 'discontinue'), body, { ifMatch: etag, idempotent: true })),
  cancel: async (id: string, body: OrderTransitionRequest, etag: string | null) =>
    normV(await postVersioned<Order>(ocmd(id, 'cancel'), body, { ifMatch: etag, idempotent: true })),
  enteredInError: async (id: string, body: OrderTransitionRequest, etag: string | null) =>
    normV(await postVersioned<Order>(ocmd(id, 'entered-in-error'), body, { ifMatch: etag, idempotent: true })),
  versions: (id: string) => get<Page<OrderVersion>>(`/orders/${seg(id)}/versions`),
  fulfillment: async (id: string) => {
    const f = await get<OrderFulfillment>(`/orders/${seg(id)}/fulfillment`);
    return { ...f, state: normalizeFulfillment(f.state) };
  },
};
